import keycloak from '../config/keycloak';

// Event types for auth lifecycle
export type AuthEvent = 
  | 'auth:refresh-start'
  | 'auth:refresh-success' 
  | 'auth:refresh-failed'
  | 'auth:expired'
  | 'network:error'
  | 'session:logout'
  | 'session:timeout';

// Request queue item
interface QueuedRequest {
  id: string;
  url: string;
  options: RequestInit;
  resolve: (response: Response) => void;
  reject: (error: Error) => void;
  retryCount: number;
}

// Auth event data
interface AuthEventData {
  type: AuthEvent;
  message?: string;
  error?: Error;
  tokenValid?: boolean;
}

class SessionNetworkManager {
  private requestQueue: QueuedRequest[] = [];
  private isRefreshing = false;
  private refreshPromise: Promise<boolean> | null = null;
  private eventListeners: Map<AuthEvent, ((data: AuthEventData) => void)[]> = new Map();
  private refreshTimer: NodeJS.Timeout | null = null;
  
  // Inactivity detection
  private lastActivity: number = Date.now();
  private inactivityTimer: NodeJS.Timeout | null = null;
  private inactivityTimeout: number = 8 * 60 * 60 * 1000; // 8 hours - very long timeout
  private activityCleanup: (() => void) | null = null;

  constructor() {
    this.setupProactiveRefresh();
    this.setupKeycloakEventListeners();
    this.setupInactivityDetection();
  }

  // ============================================
  // Event System
  // ============================================

  on(event: AuthEvent, callback: (data: AuthEventData) => void) {
    if (!this.eventListeners.has(event)) {
      this.eventListeners.set(event, []);
    }
    this.eventListeners.get(event)!.push(callback);
  }

  off(event: AuthEvent, callback: (data: AuthEventData) => void) {
    const listeners = this.eventListeners.get(event);
    if (listeners) {
      const index = listeners.indexOf(callback);
      if (index > -1) {
        listeners.splice(index, 1);
      }
    }
  }

  private emit(event: AuthEvent, data: Partial<AuthEventData> = {}) {
    const listeners = this.eventListeners.get(event);
    if (listeners) {
      const eventData: AuthEventData = { type: event, ...data };
      listeners.forEach(callback => {
        try {
          callback(eventData);
        } catch (error) {
          console.error(`Error in auth event listener for ${event}:`, error);
        }
      });
    }
  }

  // ============================================
  // Inactivity Detection
  // ============================================

  private setupInactivityDetection() {
    // Track user activity
    const activityEvents = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click'];
    
    const handleActivity = () => {
      this.updateActivity();
    };

    // Add event listeners for user activity
    activityEvents.forEach(event => {
      document.addEventListener(event, handleActivity, true);
    });

    // Start inactivity monitoring
    this.startInactivityTimer();

    // Store cleanup function
    this.activityCleanup = () => {
      activityEvents.forEach(event => {
        document.removeEventListener(event, handleActivity, true);
      });
    };
  }

  private updateActivity() {
    this.lastActivity = Date.now();
    
    // Reset inactivity timer
    this.startInactivityTimer();
  }

  private startInactivityTimer() {
    // Clear existing timer
    if (this.inactivityTimer) {
      clearTimeout(this.inactivityTimer);
    }

    // Only monitor if user is authenticated
    if (!keycloak.authenticated) {
      return;
    }

    this.inactivityTimer = setTimeout(() => {
      this.checkInactivity();
    }, 60000); // Check every minute
  }

  private checkInactivity() {
    if (!keycloak.authenticated) {
      return;
    }

    const now = Date.now();
    const timeSinceActivity = now - this.lastActivity;

    // Trigger timeout only after very long inactivity (8 hours)
    if (timeSinceActivity >= this.inactivityTimeout) {
      console.log('🕐 Session timeout due to very long inactivity (8+ hours)');
      this.handleInactivityTimeout();
      return;
    }

    // Continue monitoring
    this.startInactivityTimer();
  }

  private handleInactivityTimeout() {
    console.log('🚪 Handling inactivity timeout...');
    
    this.emit('session:timeout', {
      message: 'Session timed out due to inactivity. Please log in again.'
    });

    // Force logout
    this.forceLogout();
  }

  private forceLogout() {
    console.log('🚪 Force logout due to inactivity...');
    
    this.cleanup();
    
    try {
      keycloak.logout();
    } catch (error) {
      console.error('Error during force logout:', error);
      // Fallback: clear tokens and redirect
      keycloak.clearToken();
      window.location.href = '/';
    }
  }

  // Public method to reset activity (can be called manually)
  resetActivity() {
    this.updateActivity();
  }

  // ============================================
  // Token Management
  // ============================================

  private setupKeycloakEventListeners() {
    // Listen to Keycloak events
    if (keycloak.onTokenExpired) {
      keycloak.onTokenExpired = () => {
        console.log('🔐 Token expired, triggering refresh...');
        this.refreshToken();
      };
    }

    if (keycloak.onAuthLogout) {
      keycloak.onAuthLogout = () => {
        console.log('🔐 User logged out');
        this.cleanup();
        this.emit('session:logout');
      };
    }
  }

  private setupProactiveRefresh() {
    // Check token validity every 2 minutes (less frequent)
    this.refreshTimer = setInterval(() => {
      this.checkAndRefreshToken();
    }, 120000);
  }

  private checkAndRefreshToken() {
    if (!keycloak.authenticated || !keycloak.token) return;

    try {
      // Check if token expires in next 5 minutes (300 seconds)
      const minValidity = 300;
      
      if (keycloak.isTokenExpired(minValidity)) {
        console.log('🔐 Token expires soon, proactively refreshing...');
        this.refreshToken();
      }
    } catch (error) {
      console.error('Error checking token validity:', error);
    }
  }

  async refreshToken(): Promise<boolean> {
    // Prevent multiple concurrent refreshes
    if (this.isRefreshing && this.refreshPromise) {
      return this.refreshPromise;
    }

    this.isRefreshing = true;
    this.emit('auth:refresh-start');

    this.refreshPromise = new Promise(async (resolve) => {
      try {
        console.log('🔄 Refreshing token...');
        
        // Use Keycloak's built-in refresh with 5 minute minimum validity
        const refreshed = await keycloak.updateToken(300);
        
        if (refreshed) {
          console.log('✅ Token refreshed successfully');
          this.emit('auth:refresh-success', { tokenValid: true });
          
          // Replay queued requests
          await this.replayQueuedRequests();
          resolve(true);
        } else {
          console.log('ℹ️ Token still valid, no refresh needed');
          resolve(true);
        }
      } catch (error) {
        console.error('❌ Token refresh failed:', error);
        this.emit('auth:refresh-failed', { error: error as Error });
        
        // Check if refresh token is expired
        if (error instanceof Error && (
          error.message.includes('refresh_token') ||
          error.message.includes('invalid_grant') ||
          error.message.includes('token_expired')
        )) {
          console.log('🚪 Refresh token expired, logging out user...');
          this.handleSessionExpiry();
        }
        
        resolve(false);
      } finally {
        this.isRefreshing = false;
        this.refreshPromise = null;
      }
    });

    return this.refreshPromise;
  }

  private handleSessionExpiry() {
    this.emit('auth:expired', { message: 'Session expired. Please log in again.' });
    this.cleanup();
    
    // Clear queued requests
    this.requestQueue.forEach(req => {
      req.reject(new Error('Session expired'));
    });
    this.requestQueue = [];

    // Logout user
    try {
      keycloak.logout();
    } catch (error) {
      console.error('Error during logout:', error);
      // Fallback: redirect to home
      window.location.href = '/';
    }
  }

  // ============================================
  // Request Queue & Replay
  // ============================================

  private async replayQueuedRequests() {
    if (this.requestQueue.length === 0) return;

    console.log(`🔄 Replaying ${this.requestQueue.length} queued requests...`);
    
    const requests = [...this.requestQueue];
    this.requestQueue = [];

    for (const request of requests) {
      try {
        // Add fresh token to request
        const updatedOptions = this.addAuthHeader(request.options);
        const response = await fetch(request.url, updatedOptions);
        request.resolve(response);
      } catch (error) {
        request.reject(error as Error);
      }
    }
  }

  private queueRequest(url: string, options: RequestInit): Promise<Response> {
    return new Promise((resolve, reject) => {
      const requestId = Date.now().toString() + Math.random().toString(36);
      
      this.requestQueue.push({
        id: requestId,
        url,
        options,
        resolve,
        reject,
        retryCount: 0
      });

      console.log(`📋 Request queued: ${options.method || 'GET'} ${url}`);
    });
  }

  // ============================================
  // Network Layer
  // ============================================

  private addAuthHeader(options: RequestInit = {}): RequestInit {
    const headers = new Headers(options.headers);
    
    if (keycloak.token) {
      headers.set('Authorization', `Bearer ${keycloak.token}`);
    }

    return {
      ...options,
      headers
    };
  }

  async fetch(url: string, options: RequestInit = {}): Promise<Response> {
    // Add auth header to request
    const requestOptions = this.addAuthHeader(options);

    try {
      const response = await fetch(url, requestOptions);

      // Handle 401 errors
      if (response.status === 401) {
        console.log('🔐 Received 401, attempting token refresh...');
        
        // If already refreshing, queue this request
        if (this.isRefreshing) {
          console.log('📋 Token refresh in progress, queueing request...');
          return this.queueRequest(url, options);
        }

        // Try to refresh token
        const refreshSuccess = await this.refreshToken();
        
        if (refreshSuccess) {
          // Retry the original request with new token
          console.log('🔄 Retrying request with fresh token...');
          const retryOptions = this.addAuthHeader(options);
          return fetch(url, retryOptions);
        } else {
          // Refresh failed, return original 401 response
          return response;
        }
      }

      return response;
    } catch (error) {
      console.error('Network error:', error);
      this.emit('network:error', { error: error as Error });
      throw error;
    }
  }

  // ============================================
  // Utility Methods
  // ============================================

  isAuthenticated(): boolean {
    return keycloak.authenticated || false;
  }

  getToken(): string | undefined {
    return keycloak.token;
  }

  getUserId(): string | undefined {
    return keycloak.tokenParsed?.sub;
  }

  getTokenInfo() {
    if (!keycloak.token || !keycloak.tokenParsed) {
      return null;
    }

    const now = Math.floor(Date.now() / 1000);
    const exp = keycloak.tokenParsed.exp || 0;
    const timeUntilExpiry = exp - now;

    return {
      token: keycloak.token,
      userId: keycloak.tokenParsed.sub,
      email: keycloak.tokenParsed.email,
      expiresAt: exp,
      expiresIn: timeUntilExpiry,
      isExpired: timeUntilExpiry <= 0,
      willExpireSoon: timeUntilExpiry <= 300 // 5 minutes
    };
  }

  // ============================================
  // Cleanup
  // ============================================

  cleanup() {
    if (this.refreshTimer) {
      clearInterval(this.refreshTimer);
      this.refreshTimer = null;
    }

    if (this.inactivityTimer) {
      clearTimeout(this.inactivityTimer);
      this.inactivityTimer = null;
    }

    if (this.activityCleanup) {
      this.activityCleanup();
      this.activityCleanup = null;
    }
    
    // Clear queued requests
    this.requestQueue.forEach(req => {
      req.reject(new Error('Session manager cleanup'));
    });
    this.requestQueue = [];
    
    this.isRefreshing = false;
    this.refreshPromise = null;
  }

  // ============================================
  // Debug Methods
  // ============================================

  getDebugInfo() {
    return {
      isRefreshing: this.isRefreshing,
      queueLength: this.requestQueue.length,
      tokenInfo: this.getTokenInfo(),
      eventListeners: Array.from(this.eventListeners.keys())
    };
  }
}

// Create singleton instance
export const sessionNetworkManager = new SessionNetworkManager();

// Export for debugging
(window as any).sessionNetworkManager = sessionNetworkManager;

export default sessionNetworkManager;
